import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useAvailableFilterState } from './StateProvider';
import { useAddStartFilterMetric } from '../../../Shared/useAddStartFilterMetric';

export const ActionsContext = createContext();

const toggleReducer = (carrierItems, { value }) => {
  return carrierItems.map((carrier) =>
    carrier.id !== value
      ? carrier
      : {
          ...carrier,
          checked: !carrier.checked,
        }
  );
};

const selectOneReducer = (carrierItems, { value }) =>
  carrierItems.map((carrier) => ({
    ...carrier,
    checked: carrier.id === value,
  }));

const createApplyAll = (checked) => (carrierItems) => {
  return carrierItems.map((carrier) => ({
    ...carrier,
    checked,
  }));
};

const createLegReducer = (itemsReducer) => (state, action) => {
  const carriers = state || action.availableCarriers;
  const { legId } = action;
  return carriers.map((legCarriers, index) => {
    return legId !== index
      ? legCarriers
      : {
          legId,
          carriers: itemsReducer(legCarriers.carriers, action),
        };
  });
};

const createApplyAllReducer = (itemsReducer) => (state, action) => {
  const carriers = state || action.availableCarriers;
  return carriers.map((legCarriers, legId) => ({
    legId,
    carriers: itemsReducer(legCarriers.carriers, action),
  }));
};

const legToggleReducer = createLegReducer(toggleReducer);
const legSelectOneReducer = createLegReducer(selectOneReducer);
const legSelectAllReducer = createLegReducer(createApplyAll(true));
const legDeselectAllReducer = createLegReducer(createApplyAll(false));
const selectAllReducer = createApplyAllReducer(createApplyAll(true));

export const reducer = (carriers, action) => {
  switch (action.type) {
    case 'INIT':
      return null;
    case 'LEG_TOGGLE':
      return legToggleReducer(carriers, action);
    case 'LEG_SELECT_ONE':
      return legSelectOneReducer(carriers, action);
    case 'LEG_SELECT_ALL':
      return legSelectAllReducer(carriers, action);
    case 'LEG_DESELECT_ALL':
      return legDeselectAllReducer(carriers, action);
    case 'SELECT_ALL':
      return selectAllReducer(carriers, action);
    default:
      return carriers;
  }
};

export const useLegFilterActions = (legId) => {
  const addStartMetric = useAddStartFilterMetric();
  const availableCarriers = useAvailableFilterState();
  const dispatch = useContext(ActionsContext);

  return useMemo(() => {
    const createAction = (props) => ({
      legId,
      availableCarriers,
      ...props,
    });
    return {
      toggle: addStartMetric(({ value }) =>
        dispatch(
          createAction({
            type: 'LEG_TOGGLE',
            value,
          })
        )
      ),
      selectOne: addStartMetric((value) =>
        dispatch(
          createAction({
            type: 'LEG_SELECT_ONE',
            value,
          })
        )
      ),
      selectAll: addStartMetric(() =>
        dispatch(
          createAction({
            type: 'LEG_SELECT_ALL',
          })
        )
      ),
      deselectAll: addStartMetric(() =>
        dispatch(
          createAction({
            type: 'LEG_DESELECT_ALL',
          })
        )
      ),
    };
  }, [dispatch, availableCarriers, legId, addStartMetric]);
};

export const useFilterActions = () => {
  const addStartMetric = useAddStartFilterMetric();
  const availableCarriers = useAvailableFilterState();
  const dispatch = useContext(ActionsContext);
  return useMemo(() => {
    return {
      selectAll: addStartMetric(() => {
        dispatch({
          type: 'SELECT_ALL',
          availableCarriers,
        });
      }),
      selectLegAll: addStartMetric(({ legId }) => {
        dispatch({
          type: 'LEG_SELECT_ALL',
          legId,
          availableCarriers,
        });
      }),
    };
  }, [dispatch, availableCarriers, addStartMetric]);
};

const ActionsProvider = ({ dispatch, children }) => {
  const availableCarriers = useAvailableFilterState();
  useEffect(() => {
    if (!availableCarriers) {
      dispatch({ type: 'INIT' });
    }
  }, [dispatch, availableCarriers]);
  return (
    <ActionsContext.Provider value={dispatch}>
      {children}
    </ActionsContext.Provider>
  );
};

export default ActionsProvider;
